import EmojiIcon from '@components/EmojiIcon';
import Icon from '@components/Icon';
import Input from '@components/Input';
import Typography from '@components/Typography';
import { concatStyles } from '@styles/domain';
import { type HTMLAttributes, type Ref } from 'react';
import Chip from '../Chip';
import type { Choice } from '../domain';
import * as styles from './index.css';

interface Props<T> extends HTMLAttributes<HTMLDivElement> {
  choice: Choice<T>;
  selected?: boolean;
  ref?: Ref<HTMLDivElement>;
}

const Label = ({
  label,
  silencedText,
}: {
  label: string;
  silencedText?: string;
}) => (
  <span className={styles.labelBlock}>
    <Typography className={styles.label}>{label}</Typography>
    {silencedText && (
      <Typography variant='body3' className={styles.silencedText}>
        {silencedText}
      </Typography>
    )}
  </span>
);

const WrappedIcon = ({
  choice,
  selected,
}: {
  choice: Extract<Choice<unknown>, { type: 'icon' | 'iconInput' }>;
  selected: boolean;
}) => {
  const selectedColor = selected ? choice.selected?.color : undefined;
  return (
    <span className={styles.icon}>
      <Icon
        name={
          selected && choice.selected?.icon ? choice.selected.icon : choice.name
        }
        variant={choice.variant}
        size={choice.size}
        color={selectedColor ?? choice.color}
        fill={choice.fill}
      />
    </span>
  );
};

const Content = ({
  choice,
  selected,
}: {
  choice: Choice<unknown>;
  selected: boolean;
}) => {
  switch (choice.type) {
    case 'plain':
      return <Label label={choice.label} silencedText={choice.silencedText} />;
    case 'emoji':
      return (
        <>
          <span className={styles.icon}>
            <EmojiIcon name={choice.icon} />
          </span>
          <Label label={choice.label} silencedText={choice.silencedText} />
        </>
      );
    case 'radio':
      return (
        <>
          <Icon
            name={selected ? 'radio_button_checked' : 'radio_button_unchecked'}
            color={selected ? 'primary' : 'inherit'}
          />
          <Label label={choice.label} silencedText={choice.silencedText} />
        </>
      );
    case 'emojiInput':
      return (
        <>
          <span className={styles.icon}>
            <EmojiIcon name={choice.icon} />
          </span>
          <span className={styles.labelWithInput}>
            <Typography className={styles.label}>{choice.label}</Typography>
            <span className={styles.childInput}>
              <Input
                type='text'
                id={choice.input.name}
                name={choice.input.name}
                placeholder={choice.input.placeholder}
                value={choice.input.value ?? ''}
                validate={selected ? choice.input.validate : undefined}
                onChange={(next) => choice.input.onChange?.(next)}
              />
            </span>
          </span>
        </>
      );
    case 'tags':
      return (
        <>
          <Typography className={styles.label}>{choice.label}</Typography>
          <span className={styles.tags}>
            {choice.tags.map((tag, index) => (
              <Chip key={index} {...tag} />
            ))}
          </span>
        </>
      );
    case 'icon':
      return (
        <>
          <WrappedIcon choice={choice} selected={selected} />
          <Label label={choice.label} silencedText={choice.silencedText} />
        </>
      );
    case 'iconInput':
      return (
        <>
          <WrappedIcon choice={choice} selected={selected} />
          <span className={styles.labelWithInput}>
            <Typography className={styles.label}>{choice.label}</Typography>
            <span className={styles.childInput}>
              <Input
                type='text'
                id={choice.input.name}
                name={choice.input.name}
                placeholder={choice.input.placeholder}
                value={choice.input.value ?? ''}
                validate={selected ? choice.input.validate : undefined}
                onChange={(next) => choice.input.onChange?.(next)}
              />
            </span>
          </span>
        </>
      );
  }
};

const Cell = <T,>({
  choice,
  selected = false,
  className,
  ref,
  ...rest
}: Props<T>) => (
  <div
    ref={ref}
    className={concatStyles([
      styles.option,
      selected && styles.optionSelected,
      className,
    ])}
    {...rest}
  >
    <div
      className={concatStyles([
        styles.body,
        choice.type === 'tags' && styles.optionSpread,
      ])}
    >
      <Content choice={choice} selected={selected} />
    </div>
  </div>
);

export default Cell;
